import { Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import SignIn from './routes/SignIn'
import Callback from './routes/Callback'
import Inbox from './routes/Inbox'
import MessageRead from './routes/MessageRead'
import Compose from './routes/Compose'
import Calendar from './routes/Calendar'
import EventEdit from './routes/EventEdit'
import Dmarc from './routes/Dmarc'
import Settings from './routes/Settings'
import Admin from './routes/Admin'
import AdminTenant from './routes/AdminTenant'
import { getMe } from './lib/api'

function IndexRedirect() {
  const { data: me, isLoading, error } = useQuery({
    queryKey: ['me'], queryFn: getMe, retry: 1, staleTime: 30_000,
  })
  if (isLoading) return <div className="p-6 text-ink-500">Loading…</div>
  if (error || !me) {
    return (
      <div className="p-8 max-w-md mx-auto">
        <div className="card p-6">
          <div className="h-eyebrow mb-2">Session error</div>
          <h1 className="text-lg font-semibold text-ink-900 mb-2">Couldn't load your identity</h1>
          <p className="text-sm text-ink-500 mb-4">
            {error instanceof Error ? error.message : 'The API did not return your profile.'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => location.reload()}
              className="pill-ghost"
            >Try again</button>
            <button
              onClick={async () => {
                const { signOut } = await import('./lib/auth')
                await signOut()
              }}
              className="pill-danger"
            >Sign out</button>
          </div>
        </div>
      </div>
    )
  }
  // Prefer an owned mailbox (canonical @plustag.email address) over the raw OIDC
  // email claim — the claim can be @vault22.com / @gmail.com, which isn't a
  // platform mailbox and would 403 on the message-list endpoint.
  const owned = (me.addresses || [])[0]
  const mailbox = owned || me.email || me.sub || 'me'
  return <Navigate to={`/inbox/${encodeURIComponent(mailbox)}`} replace />
}

export default function App() {
  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route path="/oauth/callback" element={<Callback />} />

      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<IndexRedirect />} />
        <Route path="inbox/:mailbox" element={<Inbox />} />
        <Route path="sent/:mailbox" element={<Inbox folder="sent" />} />
        <Route path="message/:mailbox/:messageId" element={<MessageRead />} />
        <Route path="compose" element={<Compose />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="calendar/new" element={<EventEdit mode="new" />} />
        <Route path="calendar/:uid" element={<EventEdit mode="edit" />} />
        <Route path="dmarc" element={<Dmarc />} />
        <Route path="admin" element={<Admin />} />
        <Route path="admin/tenants/:slug" element={<AdminTenant />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
